@let noTableData = (dataSource?.length | async) === 0;

<div
  class="qt-data-table__container"
  cdkDropList
  [cdkDropListSortingDisabled]="tableConfig().dragSortingDisabled"
  [cdkDropListEnterPredicate]="canListBeDropped"
  (cdkDropListDropped)="dropped($event)"
  (cdkDropListEntered)="dragEntered()"
  (cdkDropListExited)="dragExited()">
  <div class="qt-data-table__wrapper" [style.flex-grow]="noTableData ? 0 : 1">
    <mat-table
      matSort
      [matSortDisableClear]="true"
      [dataSource]="dataSource || []"
      [trackBy]="rowTrackByFn()"
      multiTemplateDataRows>
      <!-- COLUMNS -->
      @for (column of columns(); track trackByColumns($index, column)) {
        @let colWidth = getColumnWidth(column);
        @let colMaxWidth = getColumnMaxWidth(column);
        <ng-container
          [matColumnDef]="column.id"
          [sticky]="column.sticky ?? false"
          [stickyEnd]="column.stickyEnd ?? false">
          <mat-header-cell
            *matHeaderCellDef
            mat-sort-header
            [disabled]="!column.sortable"
            [class.qt-data-table__cell--numeric]="column.numeric"
            [class.qt-data-table__cell--resizable]="tableConfig().resizable"
            [attr.data-column-id]="column.id"
            [style.flex-basis.px]="colWidth"
            [style.min-width.px]="colWidth"
            [style.max-width.px]="colMaxWidth"
            [qtResizable]="tableConfig().resizable"
            [qtResizableMin]="column.minWidth"
            [qtResizableMax]="undefined"
            [qtResizableColumnId]="column.id"
            (columnResize)="onColumnResize(column.id, $event)">
            @if (!headerDefMap.get(column.id)) {
              @let headerTitle = column | columnHeader;
              <span
                class="qt-data-table__header-title"
                [matTooltip]="headerTitle"
                >{{ headerTitle }}</span
              >
            }
            @if (headerDefMap.get(column.id); as template) {
              <ng-template
                [ngTemplateOutlet]="template"
                [ngTemplateOutletContext]="{
                  value: column,
                }"></ng-template>
            }
          </mat-header-cell>
          <mat-cell
            *matCellDef="let row"
            [class.qt-data-table__cell--numeric]="column.numeric"
            [attr.data-column-id]="column.id"
            [style.flex-basis.px]="colWidth"
            [style.min-width.px]="colWidth"
            [style.max-width.px]="colMaxWidth"
            ><span class="w-100">
              @if (columnDefMap.has(column.id) === true) {
                <ng-template
                  [ngTemplateOutlet]="columnDefMap.get(column.id)"
                  [ngTemplateOutletContext]="{
                    row: row,
                    value: valueAccessorFn(row, column.id),
                    vals: column.vals,
                  }"></ng-template>
              } @else if (
                !!column.format && formatDefMap.has(column.format) === true
              ) {
                <ng-template
                  [ngTemplateOutlet]="formatDefMap.get(column.format)"
                  [ngTemplateOutletContext]="{
                    row: row,
                    value: valueAccessorFn(row, column.id),
                    vals: column.vals,
                  }"></ng-template>
              } @else {
                {{ valueAccessorFn(row, column.id) }}
              }
            </span>
          </mat-cell>
        </ng-container>
      }
      @if (!tableConfig().hideSelect) {
        <ng-container [matColumnDef]="selectId" sticky>
          <mat-header-cell *matHeaderCellDef class="qt-data-table__select-cell">
            @if (tableConfig().multiple) {
              <mat-checkbox
                (change)="toggleAll(selection())"
                [checked]="selection().hasValue() && isAllSelected(selection())"
                [indeterminate]="
                  selection().hasValue() && !isAllSelected(selection())
                ">
              </mat-checkbox>
            }
          </mat-header-cell>
          <mat-cell
            *matCellDef="let row; let index"
            class="qt-data-table__select-cell"
            (click)="$event.stopPropagation()">
            @if (columnDefMap.has(selectId)) {
              <ng-template
                [ngTemplateOutlet]="columnDefMap.get(selectId)"
                [ngTemplateOutletContext]="{
                  row: row,
                  checked: selection().isSelected(row),
                  disabled: !isRowSelectable(row),
                  clickSelect: getClickSelected(selection(), row),
                }"></ng-template>
            } @else {
              @if (tableConfig().multiple) {
                <mat-checkbox
                  [checked]="selection().isSelected(row)"
                  [disabled]="!isRowSelectable(row)"
                  (change)="toggle(selection(), row)">
                </mat-checkbox>
              } @else {
                <mat-radio-button
                  [name]="uid + '-radio-' + index"
                  [checked]="selection().isSelected(row)"
                  [disabled]="!isRowSelectable(row)"
                  (change)="toggle(selection(), row)">
                </mat-radio-button>
              }
            }
          </mat-cell>
        </ng-container>
      }
      <ng-container [matColumnDef]="actionId" stickyEnd>
        <mat-header-cell *matHeaderCellDef class="qt-data-table__icon-cell">
          <ng-template
            [ngTemplateOutlet]="headerDefMap.get(actionId)"
            [ngTemplateOutletContext]="{ value: actionId }"></ng-template>
          @if (!headerDefMap.get(actionId)) {}
        </mat-header-cell>

        @if (!menuColumnDef()) {
          <mat-cell
            *matCellDef="let row"
            class="qt-data-table__icon-cell qt-data-table__numeric"
            (click)="$event.stopPropagation()">
            @if (tableConfig().menuButIcon && isNotGroupRow(0, row)) {
              <button
                mat-icon-button
                [matMenuTriggerFor]="menu"
                [matMenuTriggerData]="{ row: row }"
                [disabled]="isStandalonePopout()">
                <mat-icon>more_vert</mat-icon>
              </button>
            }
            @if (tableConfig().menuButIcon && tableConfig().showDragIndicator) {
              <button mat-icon-button>
                <mat-icon>drag_indicator</mat-icon>
              </button>
            }
          </mat-cell>
        } @else {
          <mat-cell
            *matCellDef="let row"
            class="qt-data-table__icon-cell qt-data-table__numeric"
            (click)="$event.stopPropagation()">
            @if (isNotGroupRow(0, row)) {
              <ng-template
                [ngTemplateOutlet]="columnDefMap.get(actionId)"
                [ngTemplateOutletContext]="{
                  row: row,
                }"></ng-template>
            }
          </mat-cell>
        }
      </ng-container>

      <ng-container [matColumnDef]="expandId" sticky>
        <mat-header-cell
          *matHeaderCellDef
          class="qt-data-table__icon-cell"
          [style.paddingLeft]="'6px'">
        </mat-header-cell>
        @if (!columnDefMap.has(expandId)) {
          <mat-cell
            *matCellDef="let row"
            class="qt-data-table__icon-cell"
            (click)="toggle(details, row); $event.stopPropagation()"
            [style.paddingLeft]="'6px'">
            <button mat-icon-button>
              <mat-icon
                [@indicatorRotate]="
                  details.isSelected(row) ? 'expanded' : 'collapsed'
                "
                >keyboard_arrow_down
              </mat-icon>
            </button>
          </mat-cell>
        } @else {
          <mat-cell
            *matCellDef="let row"
            class="qt-data-table__icon-cell"
            [style.paddingLeft]="'6px'"
            (click)="toggle(details, row); $event.stopPropagation()">
            <ng-template
              [ngTemplateOutlet]="columnDefMap.get(expandId)"
              [ngTemplateOutletContext]="{
                row: row,
                expanded: details.isSelected(row),
              }"></ng-template>
          </mat-cell>
        }
      </ng-container>

      <ng-container [matColumnDef]="groupId" sticky>
        @let column = groupedByIdColumn();
        @let minWidth =
          column?.minWidth
            ? column?.minWidth + (isGroupingCollapsible() ? 50 : 0)
            : 100;
        @let maxWidth =
          column?.maxWidth
            ? column?.maxWidth + (isGroupingCollapsible() ? 50 : 0)
            : 100;
        <mat-header-cell
          *matHeaderCellDef
          [style.flex-basis.px]="minWidth"
          [style.min-width.px]="minWidth"
          [style.max-width.px]="maxWidth">
          {{ 'GNR_GROUP_BY' | translate }}
        </mat-header-cell>
        <mat-cell
          *matCellDef="let row"
          [style.flex-basis.px]="minWidth"
          [style.min-width.px]="minWidth"
          [style.max-width.px]="maxWidth"
          (click)="toggleGroupByRow(row); $event.stopPropagation()">
          @if (isGroupRow(0, row)) {
            @if (isGroupingCollapsible()) {
              <button mat-icon-button>
                <mat-icon
                  [@indicatorRotate]="
                    isGroupRowExpanded(expandedGroupRow, row)
                      ? 'expanded'
                      : 'collapsed'
                  ">
                  keyboard_arrow_down
                </mat-icon>
              </button>
            }
            <!-- Total count of items in group -->
            <!-- <span> ({{ row.__totalGroupCount }}) </span> -->
            <!-- Value by grouped column -->
            @if (row.__predefinedGroup) {
              @if (row.__groupBy === 'null') {
                {{ 'GNR_UNGROUPED' | translate }}
              } @else {
                @if (row.__predefinedGroup.__groupByLabel) {
                  {{ row.__predefinedGroup.__groupByLabel | translate }}
                } @else {
                  {{ row.__predefinedGroup.__groupByDescription }}
                }
              }
            } @else if (!!column && columnDefMap.has(column.id) === true) {
              <ng-template
                [ngTemplateOutlet]="columnDefMap.get(column.id)"
                [ngTemplateOutletContext]="{
                  row: row,
                  value: row.__groupBy,
                  vals: column.vals,
                  group: true,
                }"></ng-template>
            } @else if (
              !!column &&
              !!column.format &&
              formatDefMap.has(column.format) === true
            ) {
              <ng-template
                [ngTemplateOutlet]="formatDefMap.get(column.format)"
                [ngTemplateOutletContext]="{
                  row: row,
                  value: row.__groupBy,
                  vals: column.vals,
                  group: true,
                }"></ng-template>
            } @else {
              {{ row.__groupBy || ('GNR_UNGROUPED' | translate) }}
            }
          }
        </mat-cell>
      </ng-container>

      <ng-container [matColumnDef]="spacerId">
        <mat-header-cell *matHeaderCellDef></mat-header-cell>
        <mat-cell *matCellDef="let row"></mat-cell>
      </ng-container>

      <!-- ROWS -->
      <mat-header-row
        [style.display]="tableConfig().hideHeaderRow ? 'none' : 'flex'"
        [attr.sticky]="tableConfig().isHeaderSticky"
        *matHeaderRowDef="columnsIds()"></mat-header-row>
      <mat-row
        *matRowDef="let row; columns: columnsIds(); when: isGroupRow"
        [class.qt-data-table__row--group-by]="isGroupedBy()"
        [class.qt-data-table__row--loading]="loading()"
        (click)="toggleGroupByRow(row); $event.stopPropagation()">
      </mat-row>
      <mat-row
        *matRowDef="let row; columns: columnsIds(); when: isNotGroupRow"
        (click)="
          rowClick.emit(row); rowEventClick.emit({ data: row, event: $event })
        "
        (dblclick)="rowDoubleClick.emit(row)"
        [class.qt-data-table__row--selected]="
          tableConfig().selectable && selection().isSelected(row)
        "
        [class.qt-data-table__row--clickable]="
          rowClick.observed || rowEventClick.observed
        "
        [class.qt-data-table__row--warning]="
          warningRowIds()?.has(row[itemKey()])
        "
        [class.qt-data-table__row--loading]="loading()"
        [qtDetailRow]="row"
        [qtDetailRowOpened]="
          details.isSelected(row) && tableConfig().displayDetailRow
        "
        [qtDetailRowTemplate]="detailsTemp"
        [ngStyle]="rowStylesCallback()(row)"
        [style.display]="
          isGroupRowExpanded(expandedGroupRow, row) ? 'none' : 'flex'
        "
        cdkDrag
        [cdkDragData]="
          tableConfig().dragIndependent
            ? row
            : tableConfig().selectable && tableConfig().multiple
              ? selection().selected
              : row
        "
        [cdkDragDisabled]="
          tableConfig().draggable
            ? tableConfig().dragIndependent
              ? false
              : tableConfig().selectable
                ? !selection().isSelected(row)
                : false
            : true
        ">
        <ng-container *cdkDragPreview>
          @if (
            tableConfig().dragIndependent && dragPreviewTemplate();
            as previewTmpl
          ) {
            <ng-container
              [ngTemplateOutlet]="previewTmpl"
              [ngTemplateOutletContext]="{ row: row }"></ng-container>
          } @else if (
            tableConfig().multiple &&
            tableConfig().draggable &&
            tableConfig().selectable
          ) {
            <div class="qt-data-table__item-drag-preview">
              {{
                selection().selected.length > 0
                  ? selection().selected.length
                  : 1
              }}
            </div>
          }
        </ng-container>

        @if (draggingPlaceholder(); as placeholderTemplate) {
          <ng-container *cdkDragPlaceholder>
            <ng-container
              *ngTemplateOutlet="placeholderTemplate"></ng-container>
          </ng-container>
        }
      </mat-row>
    </mat-table>
  </div>

  <ng-template #detailsTemp let-row>
    @if (detailsTemplate()) {
      <div class="qt-data-table__details-wrapper">
        <ng-template
          [ngTemplateOutlet]="detailsTemplate()"
          [ngTemplateOutletContext]="{ row: row }"></ng-template>
      </div>
    }
  </ng-template>

  @if (loading()) {
    <div class="qt-data-table__loading">
      <qt-data-table-loading></qt-data-table-loading>
      <p class="qt-data-table__loading-description">
        {{ 'GNR_PLS_WAIT' | translate }}
      </p>
    </div>
  }

  @if (!loading() && noTableData && !isDragPlaceholder) {
    <ng-content select="qt-empty-state"></ng-content>
    @if (!emptyState) {
      <qt-table-empty-state
        [title]="'GNR_NO_DATA' | translate"
        [subtitle]="'GNR_NO_DATA_FULL' | translate">
        <mat-icon>info</mat-icon>
      </qt-table-empty-state>
    }
  }

  <ng-content select="mat-paginator"></ng-content>
</div>
